/**
 * US 12.7 — acts on the updates my own task changes owe their source.
 *
 * Mounted once in the shell, so a task completed from Tasks, the Planner or
 * a meeting is followed up wherever it was done:
 * - from chat: a short note (key and status, nothing else) is posted in the
 *   conversation, as me, quoting the original message;
 * - from email: I'm offered a ready-made reply. Choosing it opens Mail's
 *   compose window on the thread; nothing is sent until I send it.
 * Each update is claimed on the server before acting, so two open tabs never
 * post twice.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { messagesService } from "../../chat/api/messages";
import { sourceUpdatesApi, type SourceUpdate } from "./api";

const KEY = ["tasks", "source-updates"] as const;
type TFn = (k: string, o?: Record<string, unknown>) => string;

/** The chat note: the task's key and status only (safe for every member). */
export function chatNote(u: SourceUpdate, t: TFn): string {
  if (u.event === "assigned") return t("tasks.postBack.chatAssigned", { key: u.task.key, status: u.task.status_name, defaultValue: "📋 {{key}} was picked up · {{status}}" });
  if (u.event === "completed") return t("tasks.postBack.chatDone", { key: u.task.key, defaultValue: "✅ {{key}} is done" });
  return t("tasks.postBack.chatStatus", { key: u.task.key, status: u.task.status_name, defaultValue: "🔄 {{key}} is now {{status}}" });
}

/** The reply draft: the result and a link, nothing internal. */
export function replyBody(u: SourceUpdate, t: TFn, origin = window.location.origin): string {
  const hello = u.source.sender_name
    ? t("tasks.postBack.hello", { name: u.source.sender_name.split(" ")[0], defaultValue: "Hi {{name}}," })
    : t("tasks.postBack.helloNoName", { defaultValue: "Hi," });
  const line =
    u.event === "completed"
      ? t("tasks.postBack.replyDone", { title: u.task.title, defaultValue: "Quick update: “{{title}}” is done." })
      : u.event === "assigned"
        ? t("tasks.postBack.replyAssigned", { title: u.task.title, defaultValue: "Quick update: “{{title}}” has been picked up." })
        : t("tasks.postBack.replyStatus", { title: u.task.title, status: u.task.status_name, defaultValue: "Quick update: “{{title}}” is now {{status}}." });
  return `${hello}\n\n${line}\n\n${origin}/tasks/t/${u.task.id}`;
}

export function composeReplyUrl(u: SourceUpdate, body: string): string {
  const p = new URLSearchParams({ compose: "reply", thread: u.source.thread, body });
  return `/email?${p.toString()}`;
}

export function SourceUpdatesWatcher() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => sourceUpdatesApi.pending(), refetchInterval: 15_000, retry: false });
  // Updates already being handled in this tab (offered or posting).
  const seen = useRef(new Set<string>());

  useEffect(() => {
    for (const u of q.data?.results ?? []) {
      if (seen.current.has(u.id)) continue;
      seen.current.add(u.id);
      const settle = () => void qc.invalidateQueries({ queryKey: KEY });
      if (u.kind === "chat") {
        // Claim first; only the tab that wins posts.
        sourceUpdatesApi.handle(u.id, "posted").then(
          () =>
            messagesService
              .create(u.source.thread, { body: chatNote(u, t), quoted_message_id: u.source.ref || null })
              .catch(() => sourceUpdatesApi.handle(u.id, "failed").catch(() => undefined))
              .finally(settle),
          () => undefined,
        );
        continue;
      }
      // Email: offer, never send.
      const who = u.source.sender_name || u.source.sender_email;
      let acted = false;
      toast(
        u.event === "completed"
          ? t("tasks.postBack.offerDone", { name: who, defaultValue: "Reply to {{name}} that this is done?" })
          : t("tasks.postBack.offerUpdate", { name: who, defaultValue: "Let {{name}} know about this update?" }),
        {
          description: `${u.task.key} · ${u.task.title}`,
          duration: 20_000,
          action: {
            label: t("tasks.postBack.writeReply", { defaultValue: "Write reply" }),
            onClick: () => {
              acted = true;
              void sourceUpdatesApi.handle(u.id, "sent").catch(() => undefined).finally(settle);
              navigate(composeReplyUrl(u, replyBody(u, t)));
            },
          },
          cancel: {
            label: t("tasks.postBack.notNow", { defaultValue: "Not now" }),
            onClick: () => {
              acted = true;
              void sourceUpdatesApi.handle(u.id, "dismissed").catch(() => undefined).finally(settle);
            },
          },
          onAutoClose: () => {
            if (!acted) void sourceUpdatesApi.handle(u.id, "dismissed").catch(() => undefined).finally(settle);
          },
        },
      );
    }
  }, [q.data, navigate, qc, t]);

  return null;
}
